import { PropType, computed, type Ref } from 'vue'
import VALIDATION_TYPES from '@/constants/validationTypes'
import { VALIDATION_MESSAGE_TYPES } from '@/constants/validationMessages'
import { useErrorStore } from '@/stores/error'

const VALIDATION_ERROR_MESSAGES = {
  [VALIDATION_TYPES.INPUT_REQUIRED]        : `{{ prefix }}を${VALIDATION_MESSAGE_TYPES.INPUT_REQUIRED}`,
  [VALIDATION_TYPES.SELECT_REQUIRED]       : `{{ prefix }}を${VALIDATION_MESSAGE_TYPES.SELECT_REQUIRED}`,
  [VALIDATION_TYPES.MULTI_SELECT_REQUIRED] : `{{ prefix }}を${VALIDATION_MESSAGE_TYPES.MULTI_SELECT_REQUIRED}`,
  [VALIDATION_TYPES.EMAIL]                 : `{{ prefix }}に${VALIDATION_MESSAGE_TYPES.INVALID_EMAIL}`,
  [VALIDATION_TYPES.MAIL_DOMAIN]           : `{{ prefix }}は${VALIDATION_MESSAGE_TYPES.INVALID_MAIL_DOMAIN}`,
  [VALIDATION_TYPES.URL]                   : `{{ prefix }}に${VALIDATION_MESSAGE_TYPES.INVALID_URL}`,
  [VALIDATION_TYPES.PASSWORD]              : `${VALIDATION_MESSAGE_TYPES.INVALID_PASSWORD}`,
  [VALIDATION_TYPES.NUMBER]                : `${VALIDATION_MESSAGE_TYPES.INVALID_NUMBER}`,
  [VALIDATION_TYPES.KANA]                  : `{{ prefix }}は${VALIDATION_MESSAGE_TYPES.INVALID_KANA}`,
  [VALIDATION_TYPES.DATE]                  : `${VALIDATION_MESSAGE_TYPES.INVALID_DATE}`,
  [VALIDATION_TYPES.PHONE_NUMBER]          : `${VALIDATION_MESSAGE_TYPES.INVALID_PHONE_NUMBER}`,
  [VALIDATION_TYPES.RANGE]                 : `${VALIDATION_MESSAGE_TYPES.OUT_OF_RANGE}`,
  [VALIDATION_TYPES.MAX_LENGTH]            : `${VALIDATION_MESSAGE_TYPES.OVER_MAX_LENGTH}`,
  [VALIDATION_TYPES.MIN_LENGTH]            : `${VALIDATION_MESSAGE_TYPES.BELOW_MIN_LENGTH}`,
}

export const formDefaultProps = {
  /**
   * 見出し文言
   */
  header: {
    type: String,
    default: '',
  },

  /**
   * 説明文言
   */
  description: {
    type: String,
    default: '',
  },

  /**
   * バリデーションの名前空間
   * @see 詳細は下記コンポーネントの namespace を参照
   *      @/components/validation/ValidationProvider.vue
   */
  namespace: {
    type: String,
    default: '',
  },

  /**
   * 入力要素の name 属性
   */
  name: {
    type: String,
    default: '',
  },

  /**
   * disabled 属性にわたす非活性フラグ
   */
  isDisabled: {
    type: Boolean,
    default: false,
  },

  /**
   * フォームの readonly 属性を有効にするか
   */
  readonly: {
    type: Boolean,
    default: undefined,
  },

  required: {
    type: Boolean,
    default: false,
  },

  /**
   * フォームのフォーカスアウト時に前後ホワイトスペースの trim するかどうか
   */
  hasTrim: {
    type: Boolean,
    default: false,
  },

  validationScheme: {
    type: Object as PropType<ValidationScheme>,
    default: () => ({}),
  },
}

/**
 * フォーム系要素の共通処理周り
 * @param name フォームの name 属性値（バリデーションのフィールド名）
 * @param validationScheme フォームのバリデーション設定
 *                         単純に引数に validationScheme オブジェクトを渡すと、リアクティブにはならず、動的に validationScheme が変動する場合の変化が検知できないため、
 *                         リアクティブになるように Ref 関数でラップしリアクティブオブジェクトに変換して使う。
 *                         よって、利用側で引数を渡す際に toRef で Ref オブジェクト変換して渡すか、もしくは ref<foo> という形で ref 形式で渡す必要がある。
 */
export function formPartial(name?: string, validationScheme?: Ref<ValidationScheme>) {
  const error = useErrorStore()

  const hasError = computed<boolean>(() => {
    if (!name) return false
    return !!error.getErrorMessagesByFieldName(name).length
  })

  const isRequired = computed<boolean>(() => {
    if (!validationScheme) return false
    return validationScheme.value && !!validationScheme.value.required
  })

  /**
   * エラーメッセージ生成
   * @param isSingleSelectType 対象が単一選択式のフォームかどうか
   * @param isMultiSelectType 対象が複数選択式のフォームかどうか
   * @param prefix エラーメッセージの接頭に来る見出し文言
   */
  const createErrorMessage = (isSingleSelectType: boolean, isMultiSelectType: boolean, prefix: string): ValidationMessages => {
    const requiredMessage = isSingleSelectType
      ? VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.SELECT_REQUIRED].replace(/{{ prefix }}/, `${prefix}`)
      : isMultiSelectType
        ? VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.MULTI_SELECT_REQUIRED].replace(/{{ prefix }}/, `${prefix}`)
        : VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.INPUT_REQUIRED].replace(/{{ prefix }}/, `${prefix}`)

    return {
      required: requiredMessage,
      email: VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.EMAIL].replace(/{{ prefix }}/, `${prefix}`),
      mailDomain: VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.MAIL_DOMAIN].replace(/{{ prefix }}/, `${prefix}`),
      url: VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.URL].replace(/{{ prefix }}/, `${prefix}`),
      password: VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.PASSWORD],
      number: VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.NUMBER],
      kana: VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.KANA].replace(/{{ prefix }}/, `${prefix}`),
      date: VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.DATE],
      phoneNumber: VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.PHONE_NUMBER],
      range: VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.RANGE],
      maxLength: VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.MAX_LENGTH],
      minLength: VALIDATION_ERROR_MESSAGES[VALIDATION_TYPES.MIN_LENGTH],
    }
  }

  return {
    // computed
    hasError,
    isRequired,

    createErrorMessage,
  }
}
